import React, { useRef, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import {
  ChevronUp,
  ChevronDown,
  ExternalLink,
  Eye,
  AlertCircle,
} from "lucide-react";
import {
  PpcFarmTargetLedgerData,
  PpcFarmCohortLedgerData,
  PpcFarmMailingLedgerData,
  PpcFarmSortOptions,
} from "../../types/ppcFarmTypes";

// Generic ledger data type
type GenericLedgerData =
  | PpcFarmTargetLedgerData
  | PpcFarmCohortLedgerData
  | PpcFarmMailingLedgerData;

// Column configuration interface
interface ColumnConfig {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (item: GenericLedgerData, value: any) => React.ReactNode;
  className?: string;
}

interface GenericLedgerDataTableProps<T extends GenericLedgerData> {
  data: T[];
  loading: boolean;
  error?: string | null;
  sortOptions: PpcFarmSortOptions;
  onSortChange: (sort: PpcFarmSortOptions) => void;
  onRowClick: (item: T) => void;
  pagination: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalItems: number;
    hasMore: boolean;
  };
  onLoadMore: () => void;
  columns: ColumnConfig[];
  emptyStateMessage?: string;
}

const GenericLedgerDataTable = <T extends GenericLedgerData>({
  data,
  loading,
  error,
  sortOptions,
  onSortChange,
  onRowClick,
  pagination,
  onLoadMore,
  columns,
  emptyStateMessage = "No data found. Try adjusting your search criteria.",
}: GenericLedgerDataTableProps<T>) => {
  const loadMoreRef = useRef<HTMLTableRowElement>(null);

  // Intelligent preloading intersection observer with anticipatory loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !loading && pagination.hasMore) {
          console.log(
            "🔍 Intersection observer triggered - preloading more data",
          );
          onLoadMore();
        }
      },
      {
        threshold: 0.1, // Trigger when even 10% of the element is visible
        rootMargin: "800px", // Start loading when trigger is 800px away from viewport bottom
      },
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [loading, pagination.hasMore, onLoadMore]); // Added onLoadMore for stability

  // Enhanced date formatting that handles all Firestore timestamp formats
  const formatDateForDisplay = (timestamp: any): string => {
    if (!timestamp) return "—";

    try {
      let date: Date;

      // Handle Firestore Timestamp objects (client-side SDK)
      if (timestamp && typeof timestamp === "object" && timestamp.toDate) {
        date = timestamp.toDate();
      }
      // Handle plain Date objects
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle timestamp numbers (milliseconds)
      else if (typeof timestamp === "number") {
        date = new Date(timestamp);
      }
      // Handle timestamp strings
      else if (typeof timestamp === "string") {
        date = new Date(timestamp);
      }
      // Handle serialized Firestore timestamps from Netlify function (server-side SDK)
      else if (
        timestamp &&
        typeof timestamp === "object" &&
        typeof timestamp.seconds === "number"
      ) {
        // Convert seconds to milliseconds and add nanoseconds if present
        const milliseconds =
          timestamp.seconds * 1000 +
          Math.floor((timestamp.nanoseconds || 0) / 1000000);
        date = new Date(milliseconds);
      } else {
        console.warn("Unknown timestamp format:", timestamp);
        return "—";
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn("Invalid date created from:", timestamp);
        return "—";
      }

      // Format as long date
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error, "Input:", timestamp);
      return "—";
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }
    return String(value);
  };

  const formatNumber = (num: number | null): string => {
    if (num === null || num === undefined) return "—";
    return num.toLocaleString();
  };

  const formatBoolean = (value: boolean | null): React.ReactNode => {
    if (value === null || value === undefined) return "—";
    return (
      <Badge variant={value ? "default" : "secondary"}>
        {value ? "Yes" : "No"}
      </Badge>
    );
  };

  const handleSort = (field: string) => {
    const newDirection =
      sortOptions.field === field && sortOptions.direction === "asc"
        ? "desc"
        : "asc";

    onSortChange({
      field,
      direction: newDirection,
    });
  };

  // NOTE: All sorting is now handled server-side, including numerical cohort sorting
  // using the cohortNumberNumeric field, so no client-side sorting is needed
  const sortedData = data;

  const getSortIcon = (field: string) => {
    if (sortOptions.field !== field) {
      return <ChevronUp className="h-4 w-4 text-gray-400" />;
    }

    return sortOptions.direction === "asc" ? (
      <ChevronUp className="h-4 w-4 text-blue-600" />
    ) : (
      <ChevronDown className="h-4 w-4 text-blue-600" />
    );
  };

  const handleExternalLinkClick = (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderCellValue = (column: ColumnConfig, item: T): React.ReactNode => {
    const value = (item as any)[column.key];

    // Use custom render function if provided
    if (column.render) {
      return column.render(item, value);
    }

    // Default rendering based on value type
    if (typeof value === "boolean") {
      return formatBoolean(value);
    }

    if (typeof value === "number") {
      return formatNumber(value);
    }

    // Handle all timestamp formats (including serialized from Netlify function)
    if (
      value &&
      typeof value === "object" &&
      (value.toDate || typeof value.seconds === "number")
    ) {
      return formatDateForDisplay(value);
    }

    return formatValue(value);
  };

  const renderTableSkeleton = () => (
    <div className="space-y-3">
      {[...Array(10)].map((_, i) => (
        <div key={i} className="flex space-x-4 p-4">
          {columns.map((_, colIndex) => (
            <Skeleton key={colIndex} className="h-4 w-24" />
          ))}
        </div>
      ))}
    </div>
  );

  const renderInfiniteScrollInfo = () => {
    const loadedItems = data.length;

    return (
      <div className="flex items-center justify-center mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 min-h-[60px]">
        <div className="text-sm text-gray-600 dark:text-gray-400 text-center flex items-center justify-center">
          Showing {loadedItems.toLocaleString()} of{" "}
          {pagination.totalItems.toLocaleString()} results
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Error Loading Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        {loading && data.length === 0 ? (
          <div className="p-6">{renderTableSkeleton()}</div>
        ) : data.length === 0 ? (
          <div className="p-8 text-center">
            <Eye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Data Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {emptyStateMessage}
            </p>
          </div>
        ) : (
          <>
            {/* Mobile-responsive table wrapper with scroll preloading */}
            <div
              className="overflow-x-auto"
              data-testid="ledger-table-container"
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    {columns.map((column) => (
                      <TableHead
                        key={column.key}
                        className={`${column.sortable ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800" : ""} min-w-[120px] ${column.className || ""}`}
                        onClick={
                          column.sortable
                            ? () => handleSort(column.key)
                            : undefined
                        }
                      >
                        <div className="flex items-center space-x-1">
                          <span>{column.label}</span>
                          {column.sortable && getSortIcon(column.key)}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {sortedData.map((item: T, index: number) => {
                    // Attach intersection observer ref to 15th from last row for eager loading
                    // Only attach if we have more than 10 items and there's more data to load
                    const shouldAttachRef =
                      pagination.hasMore &&
                      sortedData.length > 15 &&
                      index === sortedData.length - 15;

                    return (
                      <TableRow
                        key={item.id}
                        ref={shouldAttachRef ? loadMoreRef : undefined}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        onClick={() => onRowClick(item)}
                      >
                        {columns.map((column) => (
                          <TableCell
                            key={column.key}
                            className={column.className}
                          >
                            {renderCellValue(column, item)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Loading Spinner - Separate from trigger, only visible when loading */}
            {loading && pagination.hasMore && (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600"></div>
              </div>
            )}

            {/* Results info */}
            {renderInfiniteScrollInfo()}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default GenericLedgerDataTable;

// Predefined column configurations for each ledger type

export const TARGET_LEDGER_COLUMNS: ColumnConfig[] = [
  {
    key: "cohortNumber",
    label: "Cohort",
    sortable: true,
    render: (item, value) => {
      // Display the cohort field for rendering, but use cohortNumber for sorting
      const displayValue = (item as any).cohort || value;
      return (
        <Badge
          variant="secondary"
          className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300"
        >
          {displayValue || "—"}
        </Badge>
      );
    },
  },
  {
    key: "sellerName",
    label: "Seller Name",
    sortable: true,
    className: "min-w-[200px]",
    render: (item, value) => {
      const targetItem = item as PpcFarmTargetLedgerData;
      return (
        <div className="flex items-center space-x-2">
          <span className="font-medium">{value || "—"}</span>
          {targetItem.sellerPageLink && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  targetItem.sellerPageLink!,
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              title="View seller page"
            >
              <ExternalLink className="h-4 w-4" />
            </button>
          )}
        </div>
      );
    },
  },
  {
    key: "sellerAddress",
    label: "Seller Address",
    sortable: true,
    className: "min-w-[250px] max-w-xs truncate",
  },
  {
    key: "dateAdded",
    label: "Date Added",
    sortable: true,
    className: "min-w-[150px]",
  },
  {
    key: "addedBy",
    label: "Added By",
    sortable: true,
    className: "min-w-[120px]",
  },
  {
    key: "totalRatings",
    label: "Total Ratings",
    sortable: true,
    className: "min-w-[120px]",
  },
];

export const COHORT_LEDGER_COLUMNS: ColumnConfig[] = [
  {
    key: "cohortNumber",
    label: "Cohort",
    sortable: true,
    render: (item, value) => (
      <Badge
        variant="secondary"
        className="bg-yellow-50 text-yellow-700 dark:bg-yellow-900/10 dark:text-yellow-400 text-xs font-normal"
      >
        {value || "—"}
      </Badge>
    ),
  },
  {
    key: "dateCreated",
    label: "Date Created",
    sortable: true,
    className: "min-w-[150px]",
  },
  {
    key: "yourName",
    label: "Your Name",
    sortable: true,
    className: "min-w-[120px]",
  },
  {
    key: "count",
    label: "Count",
    sortable: true,
    className: "min-w-[100px]",
  },
  {
    key: "mailerShortDescription",
    label: "Mailer Description",
    sortable: true,
    className: "min-w-[200px] max-w-xs truncate",
  },
  {
    key: "discoveryCall",
    label: "Discovery Calls",
    sortable: true,
    className: "min-w-[120px]",
  },
  {
    key: "signedClients",
    label: "Signed Clients",
    sortable: true,
    className: "min-w-[120px]",
  },
];

// Helper function for date formatting - matches the enhanced formatDateForDisplay function
const formatDateHelper = (timestamp: any): string => {
  if (!timestamp) return "—";

  try {
    let date: Date;

    // Handle Firestore Timestamp objects (client-side SDK)
    if (timestamp && typeof timestamp === "object" && timestamp.toDate) {
      date = timestamp.toDate();
    }
    // Handle plain Date objects
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    // Handle timestamp numbers (milliseconds)
    else if (typeof timestamp === "number") {
      date = new Date(timestamp);
    }
    // Handle timestamp strings
    else if (typeof timestamp === "string") {
      date = new Date(timestamp);
    }
    // Handle serialized Firestore timestamps from Netlify function (server-side SDK)
    else if (
      timestamp &&
      typeof timestamp === "object" &&
      typeof timestamp.seconds === "number"
    ) {
      // Convert seconds to milliseconds and add nanoseconds if present
      const milliseconds =
        timestamp.seconds * 1000 +
        Math.floor((timestamp.nanoseconds || 0) / 1000000);
      date = new Date(milliseconds);
    } else {
      console.warn("Unknown timestamp format in helper:", timestamp);
      return "—";
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid date created in helper from:", timestamp);
      return "—";
    }

    // Format as long date
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch (error) {
    console.error(
      "Error formatting date in helper:",
      error,
      "Input:",
      timestamp,
    );
    return "—";
  }
};

export const MAILING_LEDGER_COLUMNS: ColumnConfig[] = [
  {
    key: "dateMailed",
    label: "Date Mailed",
    sortable: true,
    className: "min-w-[150px]",
    render: (item, value) => {
      const mailingItem = item as PpcFarmMailingLedgerData;

      // Conditional date display logic - handle both client-side and serialized timestamps
      if (value && (value.toDate || typeof value.seconds === "number")) {
        // Has actual Date Mailed value
        return formatDateHelper(value);
      }

      // No Date Mailed value - apply business logic
      if (
        mailingItem.dateEntered &&
        (mailingItem.dateEntered.toDate ||
          typeof mailingItem.dateEntered.seconds === "number")
      ) {
        // Convert to Date object for year checking
        let enteredDate: Date;
        if (mailingItem.dateEntered.toDate) {
          enteredDate = mailingItem.dateEntered.toDate();
        } else if (typeof mailingItem.dateEntered.seconds === "number") {
          const milliseconds =
            mailingItem.dateEntered.seconds * 1000 +
            Math.floor((mailingItem.dateEntered.nanoseconds || 0) / 1000000);
          enteredDate = new Date(milliseconds);
        } else {
          return "—";
        }

        const enteredYear = enteredDate.getFullYear();

        if (enteredYear <= 2023) {
          // Use Date Entered as fallback for 2023 and earlier
          return formatDateHelper(mailingItem.dateEntered);
        } else {
          // Show "Pending" for 2024 and later
          return (
            <Badge
              variant="secondary"
              className="bg-gray-50 text-gray-600 dark:bg-gray-900/10 dark:text-gray-400 text-xs font-normal"
            >
              Pending
            </Badge>
          );
        }
      }

      return "—";
    },
  },
  {
    key: "mailerId",
    label: "Mailer ID",
    sortable: true,
    className: "min-w-[120px]",
    render: (item, value) => (
      <span className="font-medium">{value || "—"}</span>
    ),
  },
  {
    key: "cohortNumber",
    label: "Cohort",
    sortable: true,
    className: "min-w-[120px]",
    render: (item, value) => {
      // Display the cohort field for rendering, but use cohortNumber for sorting
      const displayValue = (item as any).cohort || value;
      return displayValue ? (
        <Badge
          variant="secondary"
          className="bg-yellow-50 text-yellow-700 dark:bg-yellow-900/10 dark:text-yellow-400 text-xs font-normal"
        >
          {displayValue}
        </Badge>
      ) : (
        "—"
      );
    },
  },
  {
    key: "addressingType",
    label: "Addressing Type",
    sortable: true,
    className: "min-w-[150px]",
  },
  {
    key: "includeInMailingCostDashboard",
    label: "Include in Dashboard",
    sortable: true,
    className: "min-w-[150px]",
    render: (item, value) => {
      if (value === null || value === undefined) return "—";
      return (
        <Badge
          variant="secondary"
          className={
            value
              ? "bg-green-50 text-green-700 dark:bg-green-900/10 dark:text-green-400 text-xs font-normal"
              : "bg-red-50 text-red-700 dark:bg-red-900/10 dark:text-red-400 text-xs font-normal"
          }
        >
          {value ? "Yes" : "No"}
        </Badge>
      );
    },
  },
];
